import { Component, OnDestroy, OnInit } from '@angular/core';
import { closeSubscription, runTeardown } from '../services/safe-teardown';
import { ChartTickService } from '../services/chart-tick.service';
import { FrameCoalescer } from '../charts/frame-coalescer';
import { MAX_AUTO_TICKS } from '../services/axis-scaling';
import { GRIDLINE_STYLE } from '../services/chart-theme';
import { ZoomLatch } from '../services/zoom-latch';
import {
  CuringAccumulators,
  DISABLED_THRESHOLD,
} from '../services/curing-accumulators';
import {
  CURSOR_COLORS,
  OVERLAY_COLORS,
  SERIES_COLORS,
  currentTemperatureStroke,
  SERIES_NAMES,
  SERIES_THICKNESS,
  ZONE_GRADIENT_STOPS,
} from '../services/chart-theme';
import {
  loadSciChart,
  BoxAnnotation,
  EAnnotationLayer,
  CursorModifier,
  EAutoRange,
  EAxisAlignment,
  ECoordinateMode,
  ELabelPlacement,
  ENumericFormat,
  FastLineRenderableSeries,
  HeatmapColorMap,
  NumberRange,
  NumericAxis,
  RolloverModifier,
  RubberBandXyZoomModifier,
  SciChartSurface,
  TSciChart,
  TextAnnotation,
  UniformHeatmapDataSeries,
  UniformHeatmapRenderableSeries,
  VerticalLineAnnotation,
  XyDataSeries,
  ZoomPanModifier,
  zeroArray2D,
} from '../charts/scichart';
import { TemperatureDataRow } from '../models/temperature-data-row';
import { ProjectService } from '../services/project.service';
import { ChartCaptureService } from '../services/chart-capture.service';
import {
  TelemetryRingBuffer,
  DEFAULT_TICK_RETENTION,
  DEFAULT_HEATMAP_RETENTION,
} from '../services/telemetry-ring-buffer';
import {
  SpatialWindow,
  downsamplingEnabled,
  downsampleParamsFor,
  fullResolutionParamsFor,
  visibleRangeToWindow,
  xValuesForRow,
  resampleToUniform,
} from '../services/telemetry-downsample';
import { AdaptiveResolutionService, ringBufferHandle } from '../services/adaptive-resolution.service';
import {
  ReadonlyTelemetryBuffer,
  TelemetryChange,
  TelemetryStoreService,
} from '../services/telemetry-store.service';
import { ChartViewportService } from '../services/chart-viewport.service';
import { ZoneDataRow } from '../models/zone-data-row';
import { Subscription, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ProjectTemperatureData } from '../models/project-temperature-data';
import { ProjectZoneData } from '../models/project-zone-data';
import { MarkerInfo } from '../models/marker-info';
import { markerColor } from '../services/marker-color';
import {
  metresToDisplay,
  displayToMetres,
  isNoReading,
  newestReadableIndex,
} from '../services/telemetry-conversion';
import { LossDataRow } from '../models/loss-data-row';
import { ProjectLossData } from '../models/project-loss-data';
import {
  dedupeRealFaults,
  faultSeverityColor,
} from '../health/fault-health.util';
import { usableXFactor } from '../services/spatial-scale';

@Component({
  selector: 'app-temperature',
  templateUrl: './temperature.component.html',
  styleUrls: ['./temperature.component.scss'],
})
export class TemperatureComponent implements OnInit, OnDestroy {
  /**
   * CHART CONTAINER IDS ARE PER-INSTANCE, NOT HARDCODED.
   *
   * SciChartSurface.create() resolves its host through document.getElementById,
   * which returns the FIRST match in document order. The export prerenders a
   * SECOND copy of this component offscreen, and while both copies rendered the
   * same literal ids that lookup handed the offscreen surface the MOUNTED div —
   * so creating the export's surface tore down the live one already attached to
   * it. Measured on beta-dev: canvases 4 -> 2, zone-graph-1 6 children -> 0,
   * temp-graph 6 -> 0, and the zones capture then legitimately came back blank
   * and refused the report.
   *
   * The capture NAME ('zones', 'temperatureOverDistance') is unchanged and is
   * still what ChartCaptureService keys on; only the DOM id is made unique.
   */
  private static nextInstanceId = 0;
  private readonly instanceSuffix = `-${++TemperatureComponent.nextInstanceId}`;
  readonly tempGraphId = `temp-graph${this.instanceSuffix}`;
  readonly zoneGraph1Id = `zone-graph-1${this.instanceSuffix}`;
  readonly zoneGraph2Id = `zone-graph-2${this.instanceSuffix}`;



  /**
   * True once the SciChart chunk has loaded AND the surfaces exist.
   *
   * The chart container itself is deliberately NOT *ngIf'd on this:
   * SciChartSurface.create() looks the div up by id, so it has to be in the DOM
   * before creation, not after. The flag gates a loading overlay instead, which
   * is what a user should see while a 233 kB chunk is on the wire.
   */
  chartsReady = false;

  errorMessage = '';
  tempDataLoaded: boolean = false;
  /** Payload had rows but no usable CR/LS geometry — see usableXFactor. */
  geometryMissing: boolean = false;
  /** Shown over the zone strip when derived zones refused; '' otherwise. */
  zoneUnavailableText = '';
  private zoneStatusSubscription?: Subscription;
  zoneDataLoaded: boolean = false;

  // Temp Chart
  /** Bounded row storage — see TelemetryRingBuffer. Indexed by ABSOLUTE tick. */
  /** SHARED temperature rows, owned by TelemetryStoreService. */
  get temperatureDataRows(): ReadonlyTelemetryBuffer<TemperatureDataRow> {
    return this.telemetryStore.read<TemperatureDataRow>('temperature');
  }

  /**
   * Running per-position maximum, feeding the 'Maximal temperature' trace.
   *
   * This is the SAME accumulator /curing folds, deliberately: both views draw
   * the running max of the same telemetry, and a second implementation would be
   * free to disagree with the first. It also brings the trim/SIX projection
   * (rowOnLinerGrid) along, which is the part that is easy to get subtly wrong.
   *
   * The curing thresholds are left DISABLED — this view wants only `max`, and a
   * disabled threshold skips both its accumulator and its per-snapshot array.
   */
  maxAcc = new CuringAccumulators(DEFAULT_TICK_RETENTION);
  xyMaxDataSeries!: XyDataSeries;
  xyDataSeries: XyDataSeries = {} as XyDataSeries;
  tick: number = 0;
  tempData: ProjectTemperatureData = {} as ProjectTemperatureData;
  tempAboveXPoint: number = 70;
  sciChartSurface?: SciChartSurface; // Store reference to chart surface
  isInitialLoad: boolean = true; // Track if this is the first load

  rolloverModifier?: RolloverModifier;
  cursorModifier?: CursorModifier;
  zoomPanModifier?: ZoomPanModifier;
  rubberBandZoomModifier?: RubberBandXyZoomModifier;

  // Zone Chart
  /** Bounded zone row storage — see TelemetryRingBuffer. Indexed by ABSOLUTE tick. */
  /**
   * SHARED zone rows, owned by TelemetryStoreService. Indexed by ABSOLUTE tick.
   *
   * heatMaps stays PER TAB: it is derived from these rows against this tab's
   * liner geometry, and /graph derives its waterfall from TEMPERATURE rows
   * instead. Two different projections of two different families cannot be one
   * shared buffer, so only the source rows are shared.
   */
  get zoneDataRows(): ReadonlyTelemetryBuffer<ZoneDataRow> {
    return this.telemetryStore.read<ZoneDataRow>('zone');
  }

  /** Absolute index of the newest zone row already projected into heatMaps. */
  private lastHeatMapIndex = -1;
  private zoneChangeSub?: Subscription;
  heatmapDataSeries1: UniformHeatmapDataSeries = {} as UniformHeatmapDataSeries;
  heatmapDataSeries2: UniformHeatmapDataSeries = {} as UniformHeatmapDataSeries;
  zoneData: ProjectZoneData = {} as ProjectZoneData;
  zoneSciChartSurface?: SciChartSurface; // Store reference to zone chart surface
  /** Both zone surfaces, retained so their renderable series can be released. */
  private zoneSurfaces: SciChartSurface[] = [];

  /** Per-tick heatmap slices, bounded to match the row buffer. Absolute tick index. */
  heatMaps = new TelemetryRingBuffer<number[][]>(DEFAULT_HEATMAP_RETENTION);
  isRunning: boolean = false;

  rangeSliderLeftVal: number = 0;
  rangeSliderRightVal: number = 100;
  showRangeSlider: boolean = false;

  // Chart Controls Events
  public onZoomOut?: () => void;
  public onAddMarker?: () => void;
  /**
   * The range slider, coalesced to ONE redraw per animation frame.
   *
   * `<input type=range>` fires input several times per animation frame during a
   * drag, and this handler ran a full synchronous redraw for every one of them. The
   * ~75 ms figure behind the /graph tab kill was MEASURED ON THIS SCREEN, and this
   * screen is where the app lands, so the exposure here was the larger one.
   *
   * Same FrameCoalescer /graph uses: trailing edge, at most one follow-up
   * outstanding, superseded values DISCARDED rather than queued. A rate limiter
   * would still eventually perform every event and drift further behind the longer
   * the drag continues.
   */
  private readonly rangeFrames = new FrameCoalescer<[number, number]>(([l, r]) =>
    this.runRangeSliderFrame(l, r)
  );

  /**
   * Apply a coalesced slider range, and return to live if it is the full span.
   *
   * THE RESET MUST HAPPEN HERE, NOT WHERE THE GESTURE ARRIVES. It was originally
   * done synchronously in updateChartVisibleRange, and it did not work: the axis
   * write is COALESCED to the next animation frame, and that write fires
   * visibleRangeChanged, which calls armZoomLatch and re-arms the very latch that
   * had just been cleared. Reset-then-write always loses.
   *
   * The bench caught it precisely. Moving the RIGHT handle alone to the maximum left
   * the pill up and followingLive false; firing a second event on the LEFT handle
   * then fixed it — because by then the range was already full, so the write was a
   * no-op, no visibleRangeChanged fired, and nothing re-armed. A real drag of the
   * right handle fires only its own events, which is exactly the ruled gesture.
   *
   * So: mark the write programmatic (armZoomLatch spares those), perform it, then
   * reset. The flag is released on a macrotask because SciChart may deliver
   * visibleRangeChanged after the synchronous assignment returns — the same reason
   * every other programmatic range write in this file releases it that way.
   */
  private runRangeSliderFrame(left: number, right: number): void {
    const backToLive = left === 0 && right === 100;
    if (!backToLive) {
      this.onRangeSliderChange?.(left, right);
      return;
    }
    this.programmaticRangeChange = true;
    try {
      this.onRangeSliderChange?.(left, right);
      this.zoomLatch.reset();
    } finally {
      setTimeout(() => (this.programmaticRangeChange = false), 0);
    }
  }

  public onRangeSliderChange?: (left: number, right: number) => void;

  v_annotations: VerticalLineAnnotation[] = [];

  boxAnnotation: BoxAnnotation = {} as BoxAnnotation;
  /** Shades the region above the overheat limit (OT). */
  private overheatAnnotation?: BoxAnnotation;

  // Fault severity bands (Desktop Overview HorizontalFiberFaultAnnotation parity)
  lossData: ProjectLossData = {} as ProjectLossData;
  /** Bounded loss-row storage — see TelemetryRingBuffer. Absolute tick index. */
  /** SHARED loss rows, owned by TelemetryStoreService. */
  get lossDataRows(): ReadonlyTelemetryBuffer<LossDataRow> {
    return this.telemetryStore.read<LossDataRow>('loss');
  }
  private lossChangeSub?: Subscription;
  faultBandAnnotations: BoxAnnotation[] = [];

  selectedProjectSubscription?: Subscription;
  tempDataSubscription?: Subscription;
  tempRTDataSubscription?: Subscription;
  lossDataSubscription?: Subscription;
  lossRTDataSubscription?: Subscription;
  tickSubscription?: Subscription;
  vert_annot_positionsSubscription?: Subscription;
  // These three were previously created without being retained, so navigating
  // away from /temperature left them alive; every re-entry added another handler
  // that rebuilt the heatmap series. Retained and torn down now.
  zoneDataSubscription?: Subscription;
  zoneRTDataSubscription?: Subscription;
  // tickPositionSubscription REMOVED. It was declared `{} as Subscription`, never
  // assigned, and unsubscribed in ngOnDestroy — so teardown threw
  // "unsubscribe is not a function" DURING ROUTE DEACTIVATION and aborted the
  // navigation. The tick has been ChartTickService.register() since 859d5e3
  // (see unregisterTick below); this field was what that move left behind.
  /** Newest tick awaiting a redraw; see onTickChanged. */
  private pendingTick: number | string | null = null;
  /** Handle of the scheduled frame, or null when none is pending. */
  private tickFrame: number | null = null;

  constructor(
    public projectService: ProjectService,
    private chartCapture: ChartCaptureService,
    private chartViewport: ChartViewportService,
    private adaptive: AdaptiveResolutionService,
    private chartTick: ChartTickService,
    private telemetryStore: TelemetryStoreService
  ) {}

  /** Buffers registered with the adaptive memory cap; unregistered on destroy. */
  private readonly bufferKeys = ['temperature.heatMaps'];

  /**
   * This screen holds the most telemetry of any — four buffers at once — so it is
   * the one that decides whether the hard cap trips.
   */
  private registerBuffers(): void {
    // temperature and zone are registered by TelemetryStoreService, which owns
    // those buffers now — ringBufferHandle exposes shrinkTo, so only the writer
    // may hold one.
    this.adaptive.registerBuffer(
      // A heatmap slice is a 2-D grid, so its point count is rows x columns.
      ringBufferHandle(
        this.bufferKeys[0],
        this.heatMaps,
        (m) => (m?.length ?? 0) * (m?.[0]?.length ?? 0)
      )
    );
  }

  ngOnInit() {
    // ONE SHARED TICK. Registration IS the wiring — see ChartTickService.
    this.unregisterTick = this.chartTick.register((tick) => this.onTickChanged(tick));
    //console.log('Temp OnInit called');
    // Zone rows are the store's; this tab reads them and derives heatMaps from
    // them. connect() is idempotent, so it is safe from every consumer.
    this.telemetryStore.connect('zone');
    this.zoneStatusSubscription = this.projectService.zoneDerivationStatus$.subscribe((status) => {
      this.zoneUnavailableText =
        status.state === 'unavailable' ? `Zones unavailable: ${status.reason ?? 'unknown reason'}` : '';
    });
    this.telemetryStore.connect('temperature');
    this.telemetryStore.connect('loss');
    this.telemetryStore.requireRetention('loss', 'temperature', DEFAULT_TICK_RETENTION);
    this.telemetryStore.requireRetention('zone', 'temperature', DEFAULT_TICK_RETENTION);
    this.telemetryStore.requireRetention(
      'temperature',
      'temperature',
      DEFAULT_TICK_RETENTION
    );
    this.registerBuffers();

    // Store wiring above is synchronous and stays that way — telemetry must not
    // wait on a chart. Everything that can touch SciChart moves into initialise().
    void this.initialise();
  }

  /**
   * Surfaces first, subscriptions second.
   *
   * SciChart is dynamically imported now, so its classes do not exist until
   * initSciChart() resolves. Two things here used to run before that could
   * possibly be true: createTempGraph() was called synchronously after an
   * UN-awaited initSciChart(), and the changed$ subscriptions fire their handler
   * synchronously on subscribe (ReplaySubject(1)), so onZoneRowsChanged could
   * reach chart objects that did not exist.
   *
   * Deferring the subscriptions costs nothing precisely BECAUSE changed$ is a
   * ReplaySubject(1): a change emitted while the chunk was still downloading is
   * replayed to the late subscriber rather than lost.
   *
   * CuringComponent already worked this way — it hit the same class of bug
   * earlier ("ORDER MATTERS, and it was wrong") and this mirrors its shape.
   */
  private async initialise(): Promise<void> {
    try {
      await this.initSciChart();
      this.chartsReady = true;
      // Axis labels are baked in from isMetricUnit at construction. Without
      // this the cold-start path builds metric axes and then binds imperial
      // data into them — see ProjectService.whenUnitsSettled.
      await this.projectService.whenUnitsSettled();
      await this.createTempGraph();
    } catch (err) {
      console.error('Temperature > chart initialisation failed', err);
    }

    this.lossChangeSub = this.telemetryStore
      .changed$('loss')
      .subscribe(() => {
        // updateFaultSeverityBands reads the BUFFER (totalCount, nextIndex), so
        // it has to run after the store's write rather than inside the payload
        // subscription that races it.
        if (this.sciChartSurface) {
          this.updateFaultSeverityBands(this.sciChartSurface);
        }
      });
    this.zoneChangeSub = this.telemetryStore
      .changed$('zone')
      .subscribe((change) => this.onZoneRowsChanged(change));
    this.setupProjectSubscription();
  }

  /** Drops this surface out of the shared tick fan-out. */
  private unregisterTick?: () => void;

  /**
   * TEARDOWN CANNOT BE ALLOWED TO ABORT A NAVIGATION.
   *
   * Angular calls this while DEACTIVATING the route, so anything that escapes
   * here fails the navigation and the user simply does not move — which is
   * what "clicking a tab takes me back to temperature" actually was. Worse,
   * the surfaces above are already deleted by then, so the route you are stuck
   * on is holding dead charts. See safe-teardown.ts for the full stack.
   *
   * A leaked timer is a leak. A broken tab strip is the app not working.
   */
  ngOnDestroy(): void {
    runTeardown('TemperatureComponent.ngOnDestroy', () => {
      this.unregisterTick?.();
      // Drop a frame scheduled but not run; it would redraw a deleted surface.
      this.rangeFrames.cancel();
      this.resizeObserver?.disconnect();
      if (this.resizeDebounce) {
        clearTimeout(this.resizeDebounce);
        this.resizeDebounce = null;
      }

      // Unregister first: leaving a handle behind would keep this component's rows
      // reachable from the registry, which is exactly the leak the cap prevents.
      this.zoneChangeSub?.unsubscribe();
      this.lossChangeSub?.unsubscribe();
      this.telemetryStore.releaseRetention('zone', 'temperature');
      this.telemetryStore.releaseRetention('loss', 'temperature');
      this.telemetryStore.releaseRetention('temperature', 'temperature');
      for (const key of this.bufferKeys) {
        this.adaptive.unregisterBuffer(key);
      }

      // Release data series first — deleting a surface first would leave the
      // WASM-backed series orphaned.
      this.disposeZoneDataSeries();

      // Dispose of the chart surfaces to free up resources
      if (this.zoneSciChartSurface) {
        this.chartCapture.unregister('zones', this.zoneSciChartSurface);
        this.zoneSciChartSurface.delete();
      }
      if (this.sciChartSurface) {
        // Unregister BEFORE delete, and by identity — unregister() ignores a name
        // whose surface is no longer this one, which is what stops an offscreen
        // report render from wiping the live tab's registration.
        this.chartCapture.unregister('temperatureOverDistance', this.sciChartSurface);
        this.sciChartSurface.delete();
      }
      this.zoneSurfaces = [];

      closeSubscription('selectedProjectSubscription', this.selectedProjectSubscription);
      closeSubscription('tempDataSubscription', this.tempDataSubscription);
      closeSubscription('tempRTDataSubscription', this.tempRTDataSubscription);
      closeSubscription('lossDataSubscription', this.lossDataSubscription);
      closeSubscription('lossRTDataSubscription', this.lossRTDataSubscription);
      //this.tickSubscription.unsubscribe();
      closeSubscription('vert_annot_positionsSubscription', this.vert_annot_positionsSubscription);
      closeSubscription('zoneDataSubscription', this.zoneDataSubscription);
      closeSubscription('zoneRTDataSubscription', this.zoneRTDataSubscription);
      closeSubscription('zoneStatusSubscription', this.zoneStatusSubscription);
      this.cancelPendingTickFrame();

      if (this.zoomRefetchTimer) {
        clearTimeout(this.zoomRefetchTimer);
        this.zoomRefetchTimer = null;
      }

      this.heatMaps.clear();
    });
  }

  /** Watches the chart container so the surface follows the browser window. */
  private resizeObserver?: ResizeObserver;
  private resizeDebounce: ReturnType<typeof setTimeout> | null = null;

  /**
   * Keep the chart sized to its CONTAINER, not to whatever it measured once.
   *
   * The width was read a single time while building the surface and never again,
   * so the canvas and the display-point budget both froze at their start-up
   * size. The CSS was already fluid — #temp-graph is flex:1/width:100% — and
   * nothing was telling SciChart the box had changed.
   *
   * Deliberately NOT matching the desktop's fixed dimensions: this is a web app
   * and should fill the window it is given.
   */
  private observeChartResize(): void {
    const host = document.getElementById(this.tempGraphId);
    if (!host || typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => {
      // Debounced: a drag emits continuously, and re-rendering per frame would
      // cost more than the resize itself.
      if (this.resizeDebounce) {
        clearTimeout(this.resizeDebounce);
      }
      this.resizeDebounce = setTimeout(() => this.applyChartResize(), 150);
    });
    this.resizeObserver.observe(host);
  }

  private applyChartResize(): void {
    const width = document.getElementById(this.tempGraphId)?.clientWidth ?? 0;
    if (width <= 0) {
      return;
    }
    this.chartPixelWidth = width;
    // Republish so the NEXT request is sized for the new width. This only
    // updates state — it issues no fetch, so resizing cannot cause traffic.
    this.chartViewport.setPixelWidth('temperature', width);
    this.chartViewport.setPixelWidth('zone', width);

    // Nudge SciChart to lay out against the new box.
    this.sciChartSurface?.invalidateElement();
    this.zoneSciChartSurface?.invalidateElement();
    for (const surface of this.zoneSurfaces) {
      surface.invalidateElement();
    }
  }

  /** The temperature x axis, retained so the liner range can be applied to it. */
  private tempXAxis?: NumericAxis;

  /**
   * Pin the x axis to the LINER, not to the whole fiber.
   *
   * The cold-load backfill asks for the entire array (windowStart -1), so the
   * trace spans every sample the device recorded — lead-in and lead-out
   * included. With no explicit range SciChart auto-ranges to all of it: measured
   * at 1189 samples against a 689-sample liner, the axis reached ~117 m while
   * the trace and the curing band stop at CRE = 68 m, leaving the empty right
   * third Joel reported.
   *
   * CRB..CRE is the liner in metres and is exactly what the fault overlays
   * already clamp to, so pinning here makes the trace agree with shading that
   * was already liner-bounded. Zoom still works: visibleRangeChanged drives the
   * refetch, and the user can pan beyond this if they want the lead-in.
   */
  private applyLinerXRange(): void {
    const axis = this.tempXAxis;
    const linerEnd = (this.tempData?.CRE ?? 0) - (this.tempData?.CRB ?? 0);
    if (!axis || !(linerEnd > 0)) {
      return;
    }
    // ONCE PER LINER, not once per component.
    //
    // This used to be a bare boolean, so the FIRST payload to arrive won the axis
    // for the life of the page. On a cold load that is fine. But `lastSelectedProject`
    // makes the restore load the PREVIOUS project first, so its geometry pinned the
    // axis, the flag latched, and the project the user actually picked could never
    // re-pin: a 120 ft liner under a 0-200 ft strip, with the main chart correct
    // because it ranges separately. Stable, silent, and it survives a full reload
    // because the restore replays it every time.
    //
    // Keying on the geometry keeps the original intent — after the pin the range
    // belongs to the user's zoom and pan, and a repeat payload for the SAME liner
    // still will not move it — while letting a genuinely different liner re-frame.
    // pinLinerX's own zoomLatch guard still means a user zoom outranks this.
    if (
      this.appliedLinerEnd !== null &&
      Math.abs(this.appliedLinerEnd - linerEnd) < 1e-6
    ) {
      return;
    }
    this.appliedLinerEnd = linerEnd;
    this.pinLinerX(axis, linerEnd);
    // The strip is its own surface; pin it to the SAME extent or it disagrees with
    // the chart directly below it. pinLinerX is a no-op on an axis already there.
    for (const zoneAxis of this.zoneXAxes) {
      this.pinLinerX(zoneAxis, linerEnd);
    }
  }

  /**
   * Force x onto the liner extent, latch or no latch.
   *
   * Needed because zoomExtents() fits to ALL the data — the whole downsampled
   * fiber — and runs AFTER the initial pin. It rewrites visibleRange while
   * leaving autoRange alone, which is why the axis read autoRange 'Never' (my
   * pin ran) with max 117.2 (zoomExtents overwrote the value). Re-pinning after
   * each fit is what makes the pin survive.
   */
  private pinLinerX(axis: NumericAxis, linerEnd: number): void {
    if (this.zoomLatch.isZoomed) {
      return;
    }
    // Redundant sets are the fuel: each one emits visibleRangeChanged even when
    // nothing moved. Skipping a no-op breaks the cycle on its own, and the
    // suppression flag then covers the genuine first pin.
    const current = axis.visibleRange;
    if (
      current &&
      Math.abs(current.min) < 1e-6 &&
      Math.abs(current.max - linerEnd) < 1e-6 &&
      axis.autoRange === EAutoRange.Never
    ) {
      return;
    }

    this.programmaticRangeChange = true;
    try {

    // autoRange MUST be turned off in the same breath. The axis was created with
    // no autoRange, so SciChart applies its default of Once — which fires when
    // the series FIRST receives data, i.e. AFTER this pin, and silently replaces
    // it with the full-fiber extent. That is why the previous pin shipped and
    // changed nothing on screen: the value was set and then overwritten.
      axis.autoRange = EAutoRange.Never;
      axis.visibleRange = new NumberRange(0, linerEnd);
    } finally {
      // Cleared on a macrotask: SciChart may deliver visibleRangeChanged
      // asynchronously, and clearing synchronously would reopen the gap.
      setTimeout(() => (this.programmaticRangeChange = false), 0);
    }
  }

  /** True while this component is setting a range itself. */
  private programmaticRangeChange = false;

  /** Set once the user zooms; suspends every automatic re-fit until reset. */
  private zoomLatch = new ZoomLatch();

  /**
   * zoomExtents, then put x back on the liner.
   *
   * zoomExtents is wanted for Y — it is what fits the temperature scale to the
   * trace — but on X it undoes the liner pin and reintroduces the empty right
   * third. Fitting and then re-pinning keeps both.
   */
  private fitAndPinX(surface: SciChartSurface): void {
    // The user's zoom outranks the pin. This runs on EVERY data arrival — every
    // real-time poll included — so without this guard a zoom survived at most one
    // poll before being fitted away.
    if (this.zoomLatch.isZoomed) {
      return;
    }
    // The WHOLE fit is programmatic, zoomExtents included. Guarding only the pin
    // left the loop running through the other half: zoomExtents fits to the full
    // fiber (one window), the pin moves to the liner (a different window), and
    // the refetch's same-window guard never trips because the two ALTERNATE.
    // That alternation is what produced ~10 requests a second on an idle page.
    this.programmaticRangeChange = true;
    try {
      surface.zoomExtents();
      const linerEnd = (this.tempData?.CRE ?? 0) - (this.tempData?.CRB ?? 0);
      if (this.tempXAxis && linerEnd > 0) {
        this.pinLinerX(this.tempXAxis, linerEnd);
      }
    } finally {
      setTimeout(() => (this.programmaticRangeChange = false), 0);
    }
  }

  /**
   * The ZONE surfaces' x axes, pinned alongside the main one.
   *
   * The strip is a SEPARATE SciChartSurface ('zone-graph-1' / 'zone-graph-2') with
   * its own x axis, and neither declares autoRange — so SciChart applies its default
   * of Once and they frame to the first data they ever see, permanently. The main
   * chart is pinned by applyLinerXRange and was fixed; these were not, which is why
   * the strip kept reading a different liner from the chart directly below it.
   * Measured on :4200 after f21a48a: zone surface 0-223 against a correct 0-121.
   *
   * They must carry the SAME range as the main axis, not merely a correct one: the
   * strip sits directly above the chart and misalignment is the visible defect.
   */
  private zoneXAxes: NumericAxis[] = [];

  /**
   * Liner extent the x axis is currently pinned to, or null if never pinned.
   *
   * Was a boolean, which made the pin once-per-COMPONENT and let whichever project
   * loaded first keep the axis forever. Holding the value makes it once-per-LINER:
   * a repeat payload for the same liner still cannot undo a zoom, but a different
   * liner re-frames.
   */
  private appliedLinerEnd: number | null = null;

  /** Project currently selected, needed by the zoom refetch. */
  private currentProjectName = '';
  /** Chart width in CSS pixels, used to size the display-point budget. */
  private chartPixelWidth = 0;
  /** Debounce handle for zoom-driven refetches. */
  private zoomRefetchTimer: ReturnType<typeof setTimeout> | null = null;
  /** The window most recently requested, so identical zooms do not refetch. */
  private lastRequestedWindow: SpatialWindow | null = null;
  /** Guards against overlapping refetches. */
  private zoomRefetchInFlight = false;

  /** Debounce for visibleRangeChanged — SciChart fires it continuously while panning. */
  private static readonly ZOOM_REFETCH_DEBOUNCE_MS = 250;

  /**
   * Downsample params for the CURRENT visible window, or null for full resolution.
   * Used on the initial/backfill request.
   */
  currentDownsampleParams() {
    return downsampleParamsFor(this.currentWindow(), this.chartPixelWidth);
  }

  /** The visible spatial window in sample indices, mirror-aware. */
  private currentWindow(): SpatialWindow {
    const lss = this.tempData?.LSS ?? 0;
    const les = this.tempData?.LES ?? 0;
    return { start: lss, end: les };
  }

  /**
   * Wire SciChart's zoom/pan to an on-demand full-resolution refetch.
   *
   * On zoom we ask for the visible sub-window with a budget >= the samples it
   * contains, so the server returns the raw slice for exactly that window — full
   * fidelity where the user is looking, without ever transferring the whole trace.
   */
  private wireZoomRefetch(xAxis: NumericAxis): void {
    xAxis.visibleRangeChanged.subscribe((args) => {
      // Arm the latch BEFORE the downsampling check: whether we refetch is a
      // separate question from whether the user has taken control of the axis.
      this.zoomLatch.noteRangeChange(this.programmaticRangeChange);

      if (!downsamplingEnabled()) {
        return;
      }

      // OUR OWN range changes must not trigger a refetch.
      //
      // Chart creation, the liner pin and every zoomExtents emit
      // visibleRangeChanged, so a plain project load was scheduling
      // full-resolution refetches for windows nobody asked to see — measured as
      // four extra GetTemperatureData calls on a single load. A refetch is for a
      // window the USER chose to look at.
      if (this.programmaticRangeChange) {
        return;
      }
      const range = args?.visibleRange;
      if (!range) {
        return;
      }
      const window = visibleRangeToWindow(range.min, range.max, {
        lss: this.tempData?.LSS ?? 0,
        les: this.tempData?.LES ?? 0,
        linerLength: (this.tempData?.CRE ?? 0) - (this.tempData?.CRB ?? 0),
        // Mirror mode renders the full array width and reverses the trace, so the
        // visible range must be flipped back into un-reversed sample space.
        mirrored: this.lossData?.MIR === true,
      });
      this.chartViewport.setWindow('temperature', window);
      this.chartViewport.setWindow('zone', window);
      this.scheduleZoomRefetch(window);
    });
  }

  private scheduleZoomRefetch(window: SpatialWindow): void {
    // A REFETCH IS FOR A WINDOW THE USER CHOSE TO LOOK AT.
    //
    // programmaticRangeChange alone does not enforce that. It is set around our
    // own pins and fits and cleared on setTimeout(..., 0), but SciChart raises
    // visibleRangeChanged for the first data-driven autorange AFTER that tick —
    // so the flag is already false and a plain project load schedules a
    // full-resolution refetch of the extents it just fitted. That is a second
    // isRealtime=false GET per load, on top of the backfill that produced the
    // data. The latch is the honest test: it is armed above from the same
    // event, and is true only for a real gesture.
    if (!this.zoomLatch.isZoomed) {
      console.log('ZOOM-REFETCH skipped: user has not zoomed');
      return;
    }
    if (
      this.lastRequestedWindow &&
      this.lastRequestedWindow.start === window.start &&
      this.lastRequestedWindow.end === window.end
    ) {
      return;
    }
    if (this.zoomRefetchTimer) {
      clearTimeout(this.zoomRefetchTimer);
    }
    this.zoomRefetchTimer = setTimeout(
      () => this.refetchWindowAtFullResolution(window),
      TemperatureComponent.ZOOM_REFETCH_DEBOUNCE_MS
    );
  }

  /**
   * Rebuild heatMaps from the shared zone rows, driven by the store.
   *
   * Fires strictly after the write. The change KIND decides the work: a backfill
   * REPLACES the window so the projection is rebuilt wholesale, while an append
   * only needs the new rows projected — and the two are indistinguishable by
   * buffer shape, since a reset to a larger row set moves nextIndex exactly as an
   * append does.
   *
   * Projecting forward from our OWN high-water mark rather than from the
   * change's fromIndex means a missed notification self-heals on the next one
   * instead of leaving heatMaps permanently short of the rows.
   */
  private onZoneRowsChanged(change: TelemetryChange): void {
    const rows = this.zoneDataRows;

    if (change.kind === 'clear') {
      this.heatMaps.clear();
      this.lastHeatMapIndex = -1;
      return;
    }

    // The projection needs the liner geometry, which arrives with the
    // TEMPERATURE payload. Before that a zone row cannot be placed, so leave the
    // high-water mark alone and let the next notification redo the work.
    const startDataPoint = this.tempData?.LSS;
    const endDataPoint = this.tempData?.LES;
    if (
      !Number.isFinite(startDataPoint) ||
      !Number.isFinite(endDataPoint) ||
      endDataPoint === startDataPoint
    ) {
      return;
    }

    if (change.kind === 'historical') {
      this.heatMaps.clear();
      rows.forEachRetained((row) => {
        this.heatMaps.push(
          this.buildHeatMapRow(row, startDataPoint, endDataPoint)
        );
      });
      this.lastHeatMapIndex = rows.nextIndex - 1;
    } else {
      const from = Math.max(this.lastHeatMapIndex + 1, rows.firstIndex);
      for (let i = from; i < rows.nextIndex; i++) {
        const row = rows.get(i);
        if (row) {
          this.heatMaps.push(
            this.buildHeatMapRow(row, startDataPoint, endDataPoint)
          );
        }
      }
      this.lastHeatMapIndex = rows.nextIndex - 1;
    }

    // DRAW WHAT WE JUST BUILT. Without this the strip has exactly two writers —
    // createZoneGraphs, which calls renderNewestZoneSlice ONCE at construction,
    // and redrawForTick, which only fires on a SCRUB. Neither covers the case
    // that matters: surface built first, zone rows arriving second, nobody
    // scrubbing.
    //
    // That is every report export. prerenderMissing builds the component
    // offscreen, createZoneGraphs runs while heatMaps is still empty so
    // renderNewestZoneSlice finds newest = -1 and draws nothing, the backfill
    // never touches the strip, and no scrub ever happens. The capture then gets
    // the series in its CONSTRUCTION state — all zeros — and zone code 0 is
    // #0000FF. That is page 6 of the cure report: a solid blue bar.
    //
    // Measured on S3640: at the newest slice the liner reads 5 zero / 264
    // heating / 221 curing / 23 overheat, so there was a fully coloured strip
    // available the whole time and nothing asked for it.
    this.renderNewestZoneSlice();
  }

  /**
   * Fetch the zoomed window at full resolution and splice the rows into the ring
   * buffer, replacing the decimated rows already held for those ticks.
   */
  refetchWindowAtFullResolution(window: SpatialWindow): void {
    const params = fullResolutionParamsFor(window);
    const project = this.currentProjectName;
    if (!params || !project || this.zoomRefetchInFlight) {
      return;
    }
    this.zoomRefetchInFlight = true;
    this.lastRequestedWindow = window;

    this.projectService
      .loadTemperatureData(project, false, 0, params)
      .subscribe({
        next: (data) => {
          this.zoomRefetchInFlight = false;
          this.applyWindowedRows(data?.TRC ?? []);
        },
        error: () => {
          this.zoomRefetchInFlight = false;
        },
      });
  }

  /**
   * Replace retained rows with their full-resolution counterparts.
   *
   * Response rows are ordered from tick 0, so array position is the absolute tick.
   * `replace()` no-ops for ticks the ring buffer has evicted, which is correct —
   * those are outside the retention window and cannot be displayed anyway.
   */
  applyWindowedRows(rows: readonly TemperatureDataRow[]): number {
    let replaced = 0;
    for (let tick = 0; tick < rows.length; tick++) {
      if (this.telemetryStore.replace('temperature', tick, rows[tick])) {
        replaced++;
      }
    }
    if (replaced > 0) {
      this.updateTempChartForTick(this.tick.toString(), 'zoom-refetch');
    }
    return replaced;
  }

  /**
   * Release the heatmap renderable series and their WASM-backed data series.
   *
   * Previously the RT handler constructed two fresh UniformHeatmapDataSeries every
   * poll and called setupZoneDataSeries() twice, which does
   * `renderableSeries.add(...)` with no matching remove/delete — so both surfaces
   * accumulated 1 renderable series per poll (~720/hour each) and orphaned the
   * replaced data series in WASM memory. Everything is now created once and
   * released here.
   */
  private disposeZoneDataSeries(): void {
    for (const surface of this.zoneSurfaces) {
      if (!surface) continue;
      try {
        surface.renderableSeries.clear();
      } catch {
        // surface already deleted — nothing to clear
      }
    }
    for (const series of [this.heatmapDataSeries1, this.heatmapDataSeries2]) {
      if (series && typeof (series as UniformHeatmapDataSeries).delete === 'function') {
        try {
          series.delete();
        } catch {
          // already deleted
        }
      }
    }
    this.heatmapDataSeries1 = {} as UniformHeatmapDataSeries;
    this.heatmapDataSeries2 = {} as UniformHeatmapDataSeries;
    this.zoneDataLoaded = false;
  }

  /**
   * Build the heatmap slice for ONE zone row. O(width), not O(ticks x width).
   */
  private buildHeatMapRow(
    row: ZoneDataRow,
    startDataPoint: number,
    endDataPoint: number
  ): number[][] {
    const cells = endDataPoint - startDataPoint;

    // Downsampled rows (SIX present) are a non-uniform window slice, but
    // UniformHeatmapDataSeries needs evenly-spaced cells — so resample onto the
    // uniform grid, reducing collisions with max() so the worst zone severity in a
    // cell is never averaged away.
    if (row.SIX && row.SIX.length > 0) {
      const values = resampleToUniform(
        row.ZR,
        row.SIX,
        startDataPoint,
        endDataPoint,
        cells
      );
      const heatMapData = zeroArray2D([1, cells]);
      for (let c = 0; c < cells; c++) {
        heatMapData[0][c] = values[c];
      }
      return heatMapData;
    }

    const heatMapData = zeroArray2D([1, cells]);
    for (let j = startDataPoint; j < endDataPoint; j++) {
      heatMapData[0][j - startDataPoint] = row.ZR[j];
    }
    return heatMapData;
  }

  // Setup subscription to recreate chart when project changes
  setupProjectSubscription() {
    this.selectedProjectSubscription =
      this.projectService.selectedProject.subscribe(
        (projectName) => {
          this.currentProjectName = projectName ?? '';
          if (projectName && projectName.length > 0) {
            console.log('Temp > Project changed to:', projectName);
            console.log('Temp > Current unit setting:', this.projectService.isMetricUnit ? 'Metric' : 'Imperial');
            
            // Skip recreation on initial load (graph is already being created in ngOnInit)
            if (this.isInitialLoad) {
              this.isInitialLoad = false;
              console.log('Temp > Skipping recreation on initial load');
              return;
            }
            
            // Recreate the graph with the new project's unit settings
            this.recreateTempGraph();
          }
        },
        (error) => (this.errorMessage = error)
      );
  }

  // Recreate the temperature graph (called when project changes)
  async recreateTempGraph() {
    console.log('Temp > Recreating graph with units:', this.projectService.isMetricUnit ? 'Metric' : 'Imperial');

    // Dispose of the existing charts if they exist
    if (this.zoneSciChartSurface) {
      this.zoneSciChartSurface.delete();
      this.zoneSciChartSurface = undefined;
    }
    // Their surfaces are gone; holding deleted axes would pin into freed memory.
    this.zoneXAxes = [];
    if (this.sciChartSurface) {
      console.log('Temp > Disposing existing chart');
      this.sciChartSurface.delete();
      this.sciChartSurface = undefined;
    }

    // Clear data — release the WASM series, then drop the retained rows/slices.
    this.disposeZoneDataSeries();
    this.heatMaps.clear();

    // Recreate the chart
    await this.createTempGraph();
  }

  async initSciChart() {
    // SciChart is dynamically imported — nothing below exists until this
    // resolves. This is the only place chart objects are constructed, so it is
    // the only place that needs to wait.
    await loadSciChart();
    SciChartSurface.useWasmFromCDN();
    SciChartSurface.setRuntimeLicenseKey(environment.sciChartKey);
  }

  // Temperatere Graph ===========================================================================

  async createTempGraph() {
    console.log('Temp > Creating graph with units:', this.projectService.isMetricUnit ? 'Metric (m, °C)' : 'Imperial (ft, °F)');
    
    // Create the SciChartSurface in the div 'scichart-root'
    const { sciChartSurface, wasmContext } = await SciChartSurface.create(
      this.tempGraphId
    );

    // Store reference to the chart surface
    this.sciChartSurface = sciChartSurface;

    // THE MAIN TEMPERATURE PLOT WAS REGISTERED NOWHERE. Only 'zones'
    // (zone-graph-1, the thin strip above) was, so the one chart this screen
    // exists for could not be captured at all — not by the screenshot button, and
    // NOT BY THE PDF EXPORT, which asks for five names and never included this
    // one. That second consequence is a pre-existing gap affecting every report
    // and is tracked separately; this call is what makes the surface capturable.
    // THE PDF'S OVERVIEW PAGE. The server embeds this under the exact key
    // 'temperatureOverDistance' and renders nothing if it is absent — no error,
    // no warning, the page simply does not exist in the report. It has been
    // missing from every cure report ever generated: the surface WAS registered,
    // under the name 'temperature', and 'temperature' is not in PDF_CHART_NAMES
    // either, so it was captured for nobody.
    //
    // This is the right surface. viscom read desktop: the {OVERVIEW_IMAGE} token
    // is produced by CreateLineGraph with LengthLabelProvider on X and
    // TemperatureLabelProvider on Y — temperature against LINER POSITION, which is
    // exactly what temp-graph draws. The string 'temperatureOverDistance' appears
    // NOWHERE in desktop; it is purely a web-to-PDF contract key, so the name was
    // never going to identify the surface on its own.
    this.chartCapture.register('temperatureOverDistance', sciChartSurface, this.tempGraphId);

    //sciChartSurface.load

    // Create an X,Y Axis and add to the chart
    var xAxis_Temp = new NumericAxis(wasmContext, {
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit ? 'm' : 'ft',
      maxAutoTicks: MAX_AUTO_TICKS,
      majorGridLineStyle: GRIDLINE_STYLE.major,
      minorGridLineStyle: GRIDLINE_STYLE.minor,
    });

    // TODO: Set in VericureBlazor Code but not working
    //xAxis.labelProvider.formatLabel = this.formatxAxisTemperatureGraph;

    var yAxis_Temp = new NumericAxis(wasmContext, {
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit
        ? String.fromCharCode(176).concat('C')
        : String.fromCharCode(176).concat('F'),
      maxAutoTicks: MAX_AUTO_TICKS,
      majorGridLineStyle: GRIDLINE_STYLE.major,
      minorGridLineStyle: GRIDLINE_STYLE.minor,
    });
    yAxis_Temp.axisAlignment = EAxisAlignment.Left;
    yAxis_Temp.growBy = new NumberRange(0.2, 0.2);

    // A NEW axis has never been pinned, so the latch belongs to the axis, not to
    // the component's lifetime. recreateTempGraph disposes the surface and builds
    // a fresh axis — on a unit change and, crucially, on a PROJECT change, which
    // happens during every normal load. Leaving the latch set meant the pinned
    // chart was thrown away and replaced by an unpinned one that auto-ranged
    // straight back to the full-fiber extent. That is why the fix shipped twice
    // and changed nothing on screen.
    this.appliedLinerEnd = null;
    // Same reasoning for the zoom latch: the axis the user zoomed no longer
    // exists, and a new project frames a different liner.
    this.zoomLatch.reset();
    this.resetRangeSlider();
    this.tempXAxis = xAxis_Temp;
    sciChartSurface.xAxes.add(xAxis_Temp);
    this.applyLinerXRange();

    // Telemetry item 3: size the display-point budget from the chart's actual
    // width, and refetch the zoomed window at full resolution on zoom/pan.
    this.chartPixelWidth =
      document.getElementById(this.tempGraphId)?.clientWidth ?? 0;
    this.chartViewport.setPixelWidth('temperature', this.chartPixelWidth);
    // The zone heatmaps share this surface's x axis, so they share its width.
    this.chartViewport.setPixelWidth('zone', this.chartPixelWidth);
    this.observeChartResize();
    this.wireZoomRefetch(xAxis_Temp);
    sciChartSurface.yAxes.add(yAxis_Temp);

    // Add Chart Controls Modifiers
    // Create new modifier instances to avoid "already attached" errors
    // RolloverModifier keeps the SERIES TOOLTIP (the temperature under the
    // pointer) but no longer draws a line.
    //
    // Its line was the wrong tool for the desktop's hover cursor and could never
    // become it: a rollover line SNAPS TO THE NEAREST DATA POINT rather than
    // following the mouse, and it has no axis readout at all. On a dense trace
    // the snapping is invisible, which is why this read as "doesn't track"
    // rather than as an obviously different feature.
    this.rolloverModifier = new RolloverModifier();
    this.rolloverModifier.showRolloverLine = false;
    sciChartSurface.chartModifiers.add(this.rolloverModifier);

    // The hover cursor proper. CursorModifier follows the POINTER (no snapping)
    // and, with showAxisLabels, renders the x-value as a label on the x axis —
    // i.e. at the BOTTOM of the vertical line, which is the readout being asked
    // for. The label is drawn by the axis's own labelProvider, so it inherits
    // labelPrecision 2 and the m/ft postfix already configured on xAxis_Temp and
    // formats as '10.00m' with no separate format string to keep in step.
    //
    // showYLine is off: the desktop shows a vertical cursor only. showTooltip is
    // off because the rollover above already provides the series value, and two
    // tooltips tracking one pointer is noise.
    this.cursorModifier = new CursorModifier({
      showXLine: true,
      showYLine: false,
      showTooltip: false,
      showAxisLabels: true,
      crosshairStroke: CURSOR_COLORS.line,
      crosshairStrokeThickness: CURSOR_COLORS.lineThickness,
      axisLabelFill: CURSOR_COLORS.labelFill,
      axisLabelStroke: CURSOR_COLORS.labelText,
    });
    sciChartSurface.chartModifiers.add(this.cursorModifier);

    this.rubberBandZoomModifier = new RubberBandXyZoomModifier();
    this.rubberBandZoomModifier.isEnabled = false;
    sciChartSurface.chartModifiers.add(this.rubberBandZoomModifier);

    this.zoomPanModifier = new ZoomPanModifier();
    this.zoomPanModifier.isEnabled = false;
    sciChartSurface.chartModifiers.add(this.zoomPanModifier);

    // Create a line series with some data
    this.xyDataSeries = new XyDataSeries(wasmContext);
    // Named so it can appear in a legend and be told apart from the max trace.
    this.xyDataSeries.dataSeriesName = SERIES_NAMES.currentTemperature;

    const lineSeries = new FastLineRenderableSeries(wasmContext, {
      stroke: currentTemperatureStroke(),
      strokeThickness: SERIES_THICKNESS,
      dataSeries: this.xyDataSeries,
    });
    lineSeries.rolloverModifierProps.tooltipColor = '#F3FEF8';
    lineSeries.rolloverModifierProps.tooltipTextColor = '#3B3D3C';
    sciChartSurface.renderableSeries.add(lineSeries);

    // Maximal temperature — the second trace the desktop plots and this view was
    // missing entirely. Added AFTER the current trace so it draws on top,
    // matching /curing: early in a cure the two coincide, and the max is the one
    // worth seeing.
    this.xyMaxDataSeries = new XyDataSeries(wasmContext);
    this.xyMaxDataSeries.dataSeriesName = SERIES_NAMES.maximalTemperature;

    const maxLineSeries = new FastLineRenderableSeries(wasmContext, {
      stroke: SERIES_COLORS.maximalTemperature,
      strokeThickness: SERIES_THICKNESS,
      dataSeries: this.xyMaxDataSeries,
    });
    maxLineSeries.rolloverModifierProps.tooltipColor =
      SERIES_COLORS.maximalTemperature;
    maxLineSeries.rolloverModifierProps.tooltipTextColor = '#FFEFEC';
    sciChartSurface.renderableSeries.add(maxLineSeries);

    // THE CURING RANGE: a translucent wash from CT up to OT, UNDER the traces.
    //
    // Desktop parity (HorizontalTempAnnotation.xaml:10, Curing.xaml): LimeGreen
    // at low opacity, AnnotationCanvas='BelowChart', full plot width, with the
    // matching red overheat wash starting exactly where this one ends. Below the
    // data matters: a 0.2-opacity wash drawn OVER the traces dims the very line
    // the operator is reading.
    //
    // y1/y2 here are placeholders; updateBoxArea sets the real range from CT/OT.
    this.boxAnnotation = new BoxAnnotation({
      fill: OVERLAY_COLORS.cureBandFill,
      stroke: OVERLAY_COLORS.cureBand,
      strokeThickness: 1,
      annotationLayer: EAnnotationLayer.BelowChart,
      x1: this.tempData.CRB,
      x2: this.tempData.CRE,
      y1: 10,
      y2: 20,
    });
    sciChartSurface.annotations.add(this.boxAnnotation);

    // OVERHEAT REGION — everything above OT.
    //
    // Desktop shades this brown/red; web.ui drew nothing, because OT was read in
    // exactly one place in the entire client: the /info table. The limit arrived
    // in every telemetry payload and no chart ever consulted it.
    //
    // Added after the cure band so the green stays legible where they overlap.
    this.overheatAnnotation = new BoxAnnotation({
      fill: OVERLAY_COLORS.overheatBandFill,
      stroke: OVERLAY_COLORS.overheatBand,
      strokeThickness: 1,
      annotationLayer: EAnnotationLayer.BelowChart,
      x1: 0,
      x2: this.tempData.CRE,
      y1: 0,
      y2: 0,
      isHidden: true,
    });
    sciChartSurface.annotations.add(this.overheatAnnotation);

    // Subscribe to temperature data updates
    // Note: Don't subscribe to selectedProject here as it's handled in setupProjectSubscription()
    // Unsubscribe from previous subscription if it exists to prevent memory leaks
    if (this.tempDataSubscription && typeof this.tempDataSubscription.unsubscribe === 'function') {
      this.tempDataSubscription.unsubscribe();
    }
    this.tempDataSubscription = this.projectService.temperatureData.subscribe(
      (data) => {
        if (data == null) {
          console.log('Error in Temp Comp: Temp data is null.');
          return;
        } else if (data.TRC == null) {
          console.log('Error in Temp Comp: Temp data > TRC is null.');
          return;
        }

        this.tempData = data;

        // Size retention to the rows we actually got, BEFORE storing them. The
        // backfill carries the whole cure; a fixed 600-tick window threw most of
        // it away and made scrubbing refetch what had already been downloaded.
        const rowWidth = data.TRC[0]?.TR?.length ?? 0;
        if (rowWidth > 0) {
          const retention = this.adaptive.retentionFor(rowWidth);
          this.heatMaps.resizeTo(retention);
        }

        // Rebuild the running max from tick 0. It MUST be rebuilt rather than
        // folded onto: the max is cumulative from the start of the cure, so a
        // backfill that replaces the window invalidates whatever was accumulated
        // against the old one.
        this.rebuildMaxAccumulator(data.TRC);
        // THE PAYLOAD MAY ALREADY BE STALE WHEN IT ARRIVES. `temperatureData` is
        // a BehaviorSubject, so a component built by an in-SPA navigation is
        // handed the LAST EMITTED backfill — emitted when the project was opened,
        // possibly on another tab, minutes and many live ticks ago. The store has
        // been ingesting the whole time and is the authority. See catchUpTo().
        this.catchUpMaxAccumulator();
        this.applyLinerXRange();
        this.tempAboveXPoint = data.CT;

        console.log('LSS:  ' + this.tempData.LSS);
        console.log('LES:  ' + this.tempData.LES);
        console.log('CRB:  ' + this.tempData.CRB);
        console.log('CRE:  ' + this.tempData.CRE);

        // THE END OF THE CURE, NOT TICK 0 — the same ruling c676653 made for
        // /curing, applied to the page that shows the same cure.
        //
        // A finished historical project opened at tick 0 shows the AMBIENT
        // readings it started from. Measured on S3640: tick 0 is 16.65-21.85 C,
        // so both traces render as one flat band near 20 C with no structure at
        // all. In the cure report that is page 5, "Temperature", and it reads as
        // a blank page — which is exactly the complaint c676653 fixed next door
        // ("the /curing tab is a blank page", 0:00:00 everywhere, 15.75 C).
        //
        // It was also internally inconsistent: the zone strip below this trace
        // renders the NEWEST slice (renderNewestZoneSlice), so the page showed
        // the start of the cure above the end of it.
        //
        // Measured in the live harness, same fixture:
        //   tick 0    current 15.8..21.9   max 15.8..21.9    <- ambient, flat
        //   tick 291  current 26.8..47.2   max 38.8..134.2   <- the actual cure
        const rows = this.temperatureDataRows;
        const at =
          typeof rows?.get === 'function'
            ? (i: number) => rows.get(i) as { TR?: ArrayLike<number> | null } | undefined
            : () => undefined;
        this.updateTempChartForTick(
          String(newestReadableIndex(rows.firstIndex, rows.nextIndex, at)),
          'temperatureData.subscribe'
        );

        this.projectService.loadMarkers(data.MAR);
        this.updateBoxArea(this.boxAnnotation, this.tempAboveXPoint);
      this.updateOverheatArea();
        this.fitAndPinX(sciChartSurface);

        this.adjustYAxisVisibleRange(yAxis_Temp);
      },
      (error) => (this.errorMessage = error)
    );

    // Unsubscribe from previous subscription if it exists to prevent memory leaks
    if (this.tempRTDataSubscription && typeof this.tempRTDataSubscription.unsubscribe === 'function') {
      this.tempRTDataSubscription.unsubscribe();
    }
    this.tempRTDataSubscription =
      this.projectService.temperatureRTData.subscribe(
        (data) => {
          console.log('temp comp > temperatureRTData.subscribe');
          // Only counts while the user is held; ignored while following, because
          // then it is already on screen.
          this.zoomLatch.noteDataArrived();
          if (data == null) {
            console.log('Error in Temp Comp: Temp RT data is null.');
            return;
          } else if (data.TRC == null) {
            console.log('Error in Temp Comp: Temp RT data > TRC is null.');
            return;
          }

          if (data.TRC.length > 0) {
            // ONE WRITER, AND IT READS THE STORE.
            //
            // This used to fold `data.TRC` directly. That is a second source of
            // truth for the same accumulator, and it is the one that can
            // disagree with the index the render uses two statements below
            // (`temperatureDataRows.nextIndex - 1`). Folding from the store
            // instead makes "level with the store" true by construction rather
            // than by coincidence, and it is idempotent, so it also absorbs
            // whatever the backfill path left outstanding.
            //
            // ORDERING: the store subscribes to these same emissions at
            // connect() (ngOnInit), before this subscription is created in the
            // awaited chart setup, so its rows are already in when we get here.
            // If that ever inverts, the cost is bounded and self-correcting —
            // one tick late, caught on the next fold — where the old behaviour
            // was permanently misaligned.
            this.catchUpMaxAccumulator();

            console.log(
              'temp comp > pushed ' +
                data.TRC.length +
                ' row(s). Ticks: ' +
                this.temperatureDataRows.totalCount +
                ' retained: ' +
                this.temperatureDataRows.retainedCount
            );

            var lastPos = this.temperatureDataRows.nextIndex - 1;
            this.updateTempChartForTick(
              lastPos.toString(),
              'temperatureData.subscribe'
            );

            this.updateBoxArea(this.boxAnnotation, this.tempAboveXPoint);
      this.updateOverheatArea();

            this.fitAndPinX(sciChartSurface);
            this.adjustYAxisVisibleRange(yAxis_Temp);
          }
        },
        (error) => (this.errorMessage = error)
      );

    // this.tickSubscription = this.projectService.tickPosition.subscribe(
    //   (data) => {
    //     this.updateTempChartForTick(data.toString(), 'tickPosition.subscribe');
    //     this.fitAndPinX(sciChartSurface);

    //     this.adjustYAxisVisibleRange(yAxis_Temp);
    //   },
    //   (error) => (this.errorMessage = error)
    // );

    // Loss data subscriptions — feed the fault severity bands (Desktop Overview
    // HorizontalFiberFaultAnnotation parity: one full-height band per active
    // severity from its first fault position to the liner end).
    if (this.lossDataSubscription && typeof this.lossDataSubscription.unsubscribe === 'function') {
      this.lossDataSubscription.unsubscribe();
    }
    this.lossDataSubscription = this.projectService.lossData.subscribe(
      (data) => {
        if (data == null || data.LRC == null || data.LRC.length == 0) {
          return;
        }
        // Payload scalars only; the rows and everything derived FROM them are
        // handled in onLossRowsChanged, which fires after the store's write.
        this.lossData = data;
      },
      (error) => (this.errorMessage = error)
    );

    if (this.lossRTDataSubscription && typeof this.lossRTDataSubscription.unsubscribe === 'function') {
      this.lossRTDataSubscription.unsubscribe();
    }
    this.lossRTDataSubscription = this.projectService.lossRTData.subscribe(
      (data) => {
        if (data == null || data.LRC == null || data.LRC.length == 0) {
          return;
        }
        // The store appends; the bands redraw in onLossRowsChanged.
      },
      (error) => (this.errorMessage = error)
    );

    // Listen to Control Events:
    this.onZoomOut = () => {
      // Hand control back FIRST — the fit below is a no-op while latched.
      this.zoomLatch.reset();
      this.resetRangeSlider();
      this.fitAndPinX(sciChartSurface);
      if (this.showRangeSlider) {
        this.toggleRangeSlider();
      }

      this.adjustYAxisVisibleRange(yAxis_Temp);
    };

    // Double-click resets to extents, as the desktop does.
    const host = document.getElementById(this.tempGraphId);
    if (host && !this.dblClickBound) {
      this.dblClickBound = true;
      host.addEventListener('dblclick', () => this.zoomOut());
    }

    // Unsubscribe from previous subscription if it exists to prevent memory leaks
    if (this.vert_annot_positionsSubscription && typeof this.vert_annot_positionsSubscription.unsubscribe === 'function') {
      this.vert_annot_positionsSubscription.unsubscribe();
    }
    this.vert_annot_positionsSubscription =
      this.projectService.vert_annot_positions.subscribe(
        (data) => {
          console.log('TemperatureComponent > Markers subscription received:', data);
          console.log('TemperatureComponent > Number of markers:', data?.length);
          if (data && data.length > 0) {
            this.updateVerticalAnnotations(sciChartSurface, data);
          } else {
            console.log('TemperatureComponent > No markers to display or data is empty');
          }
        },
        (error) => (this.errorMessage = error)
      );
    
    // Check if markers already exist (in case they were loaded before subscription)
    const existingMarkers = this.projectService.getVerticalAnnotationPositions();
    if (existingMarkers && existingMarkers.length > 0) {
      console.log('TemperatureComponent > Found existing markers on initialization:', existingMarkers);
      this.updateVerticalAnnotations(sciChartSurface, existingMarkers);
    }

    this.onAddMarker = () => {
      this.projectService.addMarker();
    };

    this.createZoneGraphs(xAxis_Temp);
  }

  formatxAxisTemperatureGraph(dataValue: number) {
    //adjust the value that user sees on the axis from 0 to end of range instead of from startRange to endRange.
    return (dataValue - this.tempData.CRB).toFixed(2) +
      this.projectService.isMetricUnit
      ? 'm'
      : 'ft';
  }

  /**
   * Rebuild the running max from a complete backfill.
   *
   * Thresholds are all DISABLED: this view needs `max` and nothing else, and a
   * disabled threshold costs neither a running array nor a per-snapshot one.
   */
  private rebuildMaxAccumulator(rows: readonly TemperatureDataRow[]): void {
    const startDataPoint = this.tempData.LSS;
    const width = this.tempData.LES - startDataPoint;
    if (width <= 0) {
      return;
    }
    this.maxAcc.rebuildFrom(rows, startDataPoint, width, {
      curing: DISABLED_THRESHOLD,
      x1: DISABLED_THRESHOLD,
      x2: DISABLED_THRESHOLD,
      x3: DISABLED_THRESHOLD,
      x4: DISABLED_THRESHOLD,
    });
  }


  /**
   * Bring the running max level with the shared store.
   *
   * Cheap and idempotent: zero iterations in the common case, one per live tick,
   * and however many the replayed backfill was behind after an in-SPA hop.
   */
  private catchUpMaxAccumulator(): void {
    const rows = this.temperatureDataRows;
    if (!rows) {
      return;
    }
    this.maxAcc.catchUpTo(rows, this.tempData.LSS);
  }

  /**
   * Everything one tick of the time slider redraws.
   *
   * EXTRACTED SO THE WIRING IS TESTABLE. It used to live inline in the
   * subscription inside createZoneGraphs(), which builds SciChart surfaces and so
   * cannot be constructed in a spec. That made the most important decision here —
   * WHICH series follow the tick — unassertable, and a mutation matrix proved it:
   * reverting the envelope to frozen left every test green.
   *
  // BOTH SERIES. The instantaneous profile must obviously show the profile
  // at the selected time. The running-peak ENVELOPE must follow too, and
  // that was the open question: max-up-to-tick, or whole-cure max?
  //
  // SETTLED FROM DESKTOP'S BACKING ARRAYS, not from watching a chart. viscom
  // read the collections at all 618 timeline positions: 193 DISTINCT
  // profiles, monotone increasing, and SHRINKING ON SCRUB-BACK. A whole-cure
  // maximum would be one profile repeated 618 times. 193 distinct monotone
  // profiles that shrink when you go back is max-accumulated-to-the-
  // displayed-tick by definition.
  //
  // The code already agreed: maxAcc.at(sliderVal) below is indexed BY TICK,
  // which only makes sense for a per-tick snapshot — a whole-cure max would
  // be a single array with no at(tick) to call.
  //
  // So includeMax defaults to true and both series redraw together. One
  // subscription drives both rather than two subscriptions racing on the
  // same event; desktop's two are an implementation detail of its binding
  // model, not a behaviour we need to copy.
   */
  private onTickChanged(tick: number | string): void {
    // COALESCE TO ONE REDRAW PER FRAME, TRAILING EDGE.
    //
    // The slider is `(input)="updateChartDataForTick(myRange.value)"`, and `input`
    // fires on every pointer move — several per animation frame during a drag.
    // Each one used to run a full synchronous redraw: both temperature series, the
    // zone strip and the fault bands. That is N redraws per frame where one would
    // do, and every intermediate one is overwritten before it can be seen.
    //
    // MEASURED, and this is why coalescing rather than dropping a series: frame
    // cost does NOT scale with cure length — 630 ticks and 230 ticks both land
    // around 75 ms — so the expense is per-redraw work, not data volume. Removing
    // the running-peak envelope buys ~14 ms of that and still leaves every frame
    // over budget, which costs a series and fixes nothing.
    //
    // Trailing edge: the newest tick wins. Intermediate positions during a drag are
    // exactly the ones nobody sees, so rendering them is pure waste.
    this.pendingTick = tick;
    if (this.tickFrame !== null) {
      return;
    }
    this.tickFrame = requestAnimationFrame(() => {
      this.tickFrame = null;
      const pending = this.pendingTick;
      this.pendingTick = null;
      if (pending !== null) {
        this.redrawForTick(pending);
      }
    });
  }

  /**
   * Drop any frame scheduled but not yet run.
   *
   * Without this, teardown leaves a callback that redraws a surface which is being
   * deleted — SciChart throws on a deleted context, and the only sign is a console
   * error during navigation. Extracted so it is assertable: ngOnDestroy also
   * releases three telemetry retentions and unregisters two captures, none of
   * which a spec should have to stand up to check that a frame was cancelled.
   */
  private cancelPendingTickFrame(): void {
    if (this.tickFrame !== null) {
      cancelAnimationFrame(this.tickFrame);
      this.tickFrame = null;
    }
    this.pendingTick = null;
  }

  /** Everything one tick redraws. Called at most once per frame via onTickChanged. */
  private redrawForTick(tick: number | string): void {
    const val = String(tick);
    this.updateTempChartForTick(val, 'tickPosition.subscribe');
    this.updateZoneChartForTick(val);
    // Severity bands follow the scrubbed tick, like the zone strip.
    if (this.sciChartSurface) {
      this.updateFaultSeverityBands(this.sciChartSurface);
    }
  }

  /**
   * Redraw the temperature chart at a tick.
   *
   * @param includeMax also redraw the running-peak envelope. FALSE from the
   *   slider path — see the subscription below for why the envelope is left
   *   alone for now.
   */
  updateTempChartForTick(val: string, source: string, includeMax = true) {
    const sliderVal: number = +val;
    this.tick = sliderVal;

    // THE CLEAR USED TO BE HERE, AND IT IS WHY THE CHART WENT BLANK.
    //
    // Joel: "I JUST EXPERIENCED A BLANK TEMPERATURE... a couple of refreshes
    // fixed it." Wiping the series at the top of the render left FOUR early
    // returns between the wipe and the append below — empty buffer, no row, a
    // row with no readings, and no geometry. Any of them returned with the
    // series already emptied and nothing to put back, so the trace vanished and
    // only a reload could restore it.
    //
    // It stuck rather than self-healing because the bail sets
    // tempDataLoaded = false: the next render then skipped the clear AND the
    // append, so the chart stayed blank until a render made it all the way
    // through. That is the same defect as the waterfall's (3261d4d), where
    // rebuildWaterfallChart cleared the renderables and only one of three
    // callers re-added them.
    //
    // The clear now sits immediately before the append it belongs to — the shape
    // curing.component.ts already uses, and states: "Safe to clear now — every
    // append below is guaranteed to follow." A render that cannot draw leaves the
    // last good frame alone, which is also what health.component decided for its
    // fault sections: last-known-good beats a blank.

    if (this.temperatureDataRows.totalCount == 0) {
      this.tempDataLoaded = false;
      return;
    }

    // A RENDER NEVER ASKS FOR HISTORY.
    //
    // Reloading cannot recover an evicted tick: the backfill refetches the whole
    // cure and reset() keeps only the newest N rows, so the missing tick is
    // evicted again the moment it arrives. On a cure longer than the retention
    // window that is an unbreakable loop, re-firing on every render. nearest()
    // is the whole answer — it always yields a row to draw, and asking the server
    // changes nothing about which rows we keep.
    let row = this.temperatureDataRows.get(sliderVal);
    if (row == null) {
      row = this.temperatureDataRows.nearest(sliderVal)?.row;
    }
    if (row == null) {
      this.tempDataLoaded = false;
      return;
    }

    if (row.TR == null || row.TR.length == 0) {
      this.tempDataLoaded = false;
      return;
    } else {
      this.tempDataLoaded = true;
    }

    const startDataPoint = this.tempData.LSS;
    const endDataPoint = this.tempData.LES;

    // NO GEOMETRY, NO AXIS. See usableXFactor for why null and not a fallback.
    //
    // Without this the division below is 0/0 on every finished project beta-dev
    // serves, and the NaN reaches SciChart as an axis range: it throws, the chart
    // stays blank, and the only sign is a console error. Declining here turns a
    // silent blank into the stated empty state.
    const xFactor = usableXFactor(
      this.tempData.CRB,
      this.tempData.CRE,
      startDataPoint,
      endDataPoint
    );
    if (xFactor === null) {
      this.geometryMissing = true;
      this.tempDataLoaded = false;
      return;
    }
    this.geometryMissing = false;

    // Two row shapes (telemetry item 3):
    //  - SIX present  -> the row is a WINDOW SLICE; position in the array is not
    //    the sample index, so x must come from SIX. Note the server sets SIX (and
    //    DS) whenever downsample params were sent, INCLUDING its full-resolution
    //    branch — so SIX, not DS, is what decides how to read the array.
    //  - SIX absent   -> the row is the whole array; position IS the sample index
    //    and we slice [LSS, LES) exactly as before.
    let xValues: number[];
    let yValues: number[];

    if (row.SIX && row.SIX.length > 0) {
      const count = Math.min(row.TR.length, row.SIX.length);
      yValues = new Array<number>(count);
      for (let k = 0; k < count; k++) {
        yValues[k] = row.TR[k];
      }
      xValues = xValuesForRow(row.SIX, count, startDataPoint, xFactor);
    } else {
      const count = endDataPoint - startDataPoint;
      xValues = new Array<number>(count);
      yValues = new Array<number>(count);
      for (let j = startDataPoint; j < endDataPoint; j++) {
        xValues[j - startDataPoint] = (j - startDataPoint) * xFactor;
        yValues[j - startDataPoint] = row.TR[j];
      }
    }

    // OMIT NO-READING SAMPLES. Required by the latestTick change above, not
    // optional alongside it: landing on the END of a finished cure lands on the
    // trailing -273.15 rows the DTS writes after it stops measuring, and
    // plotting those draws a flat false trace at absolute zero. Measured on
    // S3640, tick 295: current temperature -273.1 across all 129 samples.
    //
    // adjustYAxisVisibleRange floors this axis at -10, so the SCALE survives
    // here where /curing's ran to -354 — but a clamped axis only hides the
    // damage, it does not stop a line being drawn along the bottom of the chart
    // for readings that do not exist. Same rule as /graph and /curing: OMIT,
    // NEVER CLAMP.
    const keptX: number[] = [];
    const keptY: number[] = [];
    for (let i = 0; i < yValues.length; i++) {
      if (!isNoReading(yValues[i])) {
        keptX.push(xValues[i]!);
        keptY.push(yValues[i]!);
      }
    }
    // REPLACE ONLY WHEN THERE IS A REPLACEMENT, and this is where the two halves
    // of 020a383 meet the blank-chart fix.
    //
    // Omitting sentinels is right, and landing on the last tick is right, but
    // together they can leave NOTHING to append — because the last tick of a
    // finished cure is exactly where the trailing -273.15 rows live, which
    // 020a383 measured itself ("tick 295: current temperature -273.1 across all
    // 129 samples"). Clearing first and then appending an empty array is the
    // same blank chart 9d22287 fixed, reached through a different door: the
    // clear stopped being "guaranteed to be followed by an append" the moment
    // the append could drop every sample.
    //
    // So each series is cleared only when it has something to put back, and the
    // two are decided independently — on that S3640 tick the current trace is
    // empty while the max trace still runs 38.8..134.2, and the user should keep
    // seeing the max rather than lose both to one absent row.
    if (keptX.length > 0) {
      this.xyDataSeries.clear();
      this.xyDataSeries.appendRange(keptX, keptY);
    }

    // Maximal temperature for this tick. The snapshot is already projected onto
    // the liner grid (index k == sample k of [LSS, LES)), so its x is uniform —
    // no SIX indirection here, unlike the current-temperature row above.
    // A MISS HERE IS A BUG EVERY TIME, SO IT IS NEVER SILENT.
    //
    // This was `?? this.maxAcc.at(this.maxAcc.firstIndex)`, and that one
    // character of tolerance hid H13 for a day: when the accumulator fell behind
    // the store the `??` quietly substituted TICK 0 OF THE CURE — a legal-looking
    // envelope, plausibly shaped, entirely wrong, and indistinguishable from a
    // correct render by eye. It is the same anti-pattern as the capture path
    // serving a cached image for a chart it could not draw: a fallback that
    // yields a plausible value instead of failing.
    //
    // Loud failure is policy now, not preference. Two changes:
    //   - the miss is reported, with every index needed to diagnose it
    //   - the degraded value is the NEAREST retained snapshot, not the FIRST.
    //     Nearest is at worst slightly stale; first is the start of the cure, so
    //     it is arbitrarily wrong and gets more wrong the longer the cure runs.
    // Rendering nothing was considered and rejected — a blank chart is its own
    // defect here, and the never-blank rule already governs this file.
    let maxSnapshot = this.maxAcc.at(sliderVal);
    if (!maxSnapshot) {
      const rows = this.temperatureDataRows;
      console.error(
        'TEMP max snapshot MISS at tick ' +
          sliderVal +
          ': accumulator holds ' +
          this.maxAcc.firstIndex +
          '..' +
          (this.maxAcc.foldedCount - 1) +
          ', store holds ' +
          (rows ? rows.firstIndex : '?') +
          '..' +
          (rows ? rows.nextIndex - 1 : '?') +
          '. Rendering the NEAREST retained snapshot; the envelope is stale.'
      );
      maxSnapshot = this.maxAcc.nearest(sliderVal);
    }
    if (includeMax && maxSnapshot?.max?.length) {
      const maxX: number[] = [];
      const maxY: number[] = [];
      for (let k = 0; k < maxSnapshot.max.length; k++) {
        const v = maxSnapshot.max[k];
        if (!isNoReading(v)) {
          maxX.push(k * xFactor);
          maxY.push(v as number);
        }
      }
      if (maxX.length > 0 && includeMax) {
        this.xyMaxDataSeries?.clear();
        this.xyMaxDataSeries.appendRange(maxX, maxY);
      }
    }
  }

  // Zone Graph ===========================================================================

  async createZoneGraphs(tempXAsix: NumericAxis) {
    // Create the SciChartSurface
    const { sciChartSurface: sciChartSurface1, wasmContext: wasmContext1 } =
      await SciChartSurface.create(this.zoneGraph1Id);
    const { sciChartSurface: sciChartSurface2, wasmContext: wasmContext2 } =
      await SciChartSurface.create(this.zoneGraph2Id);

    // Store reference and register for PDF export capture
    this.zoneSciChartSurface = sciChartSurface1;

    // REGISTER BOTH SURFACES. This list was declared, cleared and iterated —
    // and never populated. disposeZoneDataSeries() walks it to clear
    // renderableSeries, so with it empty nothing was ever detached: every zone
    // emission attached ANOTHER renderable series while deleting the data series
    // the previous one pointed at. Hence three renderables on the surface with
    // get(0).dataSeries.count() === 0, a live series buried behind two corpses,
    // and a strip that stayed dark however correct everything else was.
    this.zoneSurfaces = [sciChartSurface1, sciChartSurface2];
    this.chartCapture.register('zones', sciChartSurface1, this.zoneGraph1Id);

    // Create an X,Y Axis and add to the chart
    const xAxis = new NumericAxis(wasmContext1, {
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit ? 'm' : 'ft',
      axisAlignment: EAxisAlignment.Top,
      maxAutoTicks: MAX_AUTO_TICKS,
      majorGridLineStyle: GRIDLINE_STYLE.major,
      minorGridLineStyle: GRIDLINE_STYLE.minor,
    });

    const yAxis = new NumericAxis(wasmContext1, {
      autoRange: EAutoRange.Always,
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit ? 'C' : 'F',
      drawLabels: false,
      axisAlignment: EAxisAlignment.Left,
      maxAutoTicks: MAX_AUTO_TICKS,
      majorGridLineStyle: GRIDLINE_STYLE.major,
      minorGridLineStyle: GRIDLINE_STYLE.minor,
    });

    sciChartSurface1.xAxes.add(xAxis);
    sciChartSurface1.yAxes.add(yAxis);

    // Create an X,Y Axis and add to the chart
    // wasmContext2: these axes belong to sciChartSurface2. Built on the other
    // surface's context they are as invisible as the series were.
    const xAxis2 = new NumericAxis(wasmContext2, {
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit ? 'm' : 'ft',
      axisAlignment: EAxisAlignment.Top,
      isVisible: false,
      maxAutoTicks: MAX_AUTO_TICKS,
      majorGridLineStyle: GRIDLINE_STYLE.major,
      minorGridLineStyle: GRIDLINE_STYLE.minor,
    });

    const yAxis2 = new NumericAxis(wasmContext2, {
      autoRange: EAutoRange.Always,
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit ? 'C' : 'F',
      drawLabels: false,
      axisAlignment: EAxisAlignment.Left,
      isVisible: false,
      maxAutoTicks: MAX_AUTO_TICKS,
      majorGridLineStyle: GRIDLINE_STYLE.major,
      minorGridLineStyle: GRIDLINE_STYLE.minor,
    });

    sciChartSurface2.xAxes.add(xAxis2);
    sciChartSurface2.yAxes.add(yAxis2);

    // Fresh axes have never been pinned; re-register and let the next payload pin
    // them. Replaces rather than appends, so a surface rebuild cannot leave a
    // deleted axis in the list.
    this.zoneXAxes = [xAxis, xAxis2];

    // Api subscription
    this.zoneDataSubscription = this.projectService.zoneData.subscribe(
      (data) => {
        if (data == null) {
          console.log('Error in Temp Comp: Zone data is null.');
          return;
        } else if (data.ZRC == null) {
          console.log('Error in Temp Comp: Zone data > ZRC is null.');
          return;
        }

        // PAYLOAD SCALARS ONLY. The rows are the store's, and heatMaps —
        // which is derived FROM the rows — is rebuilt in onZoneRowsChanged.
        // Deriving here would race the store's write: whichever of the two
        // subscribers ran first would decide whether heatMaps saw the new rows.
        this.zoneData = data;

        const startDataPoint = this.tempData.LSS;
        const endDataPoint = this.tempData.LES;

        const xFactor =
          (this.tempData.CRE - this.tempData.CRB) /
          (endDataPoint - startDataPoint);

        const firstSlice =
          this.heatMaps.get(this.heatMaps.firstIndex) ??
          zeroArray2D([1, endDataPoint - startDataPoint]);

        // Release anything from a previous project/backfill before allocating new
        // WASM-backed series, then create them ONCE. The RT path mutates these in
        // place via setZValues() rather than constructing replacements.
        this.disposeZoneDataSeries();

        this.heatmapDataSeries1 = new UniformHeatmapDataSeries(wasmContext1, {
          xStart: 0,
          xStep: xFactor,
          yStart: 100,
          yStep: 1,
          zValues: firstSlice,
        });

        this.heatmapDataSeries2 = new UniformHeatmapDataSeries(wasmContext2, {
          xStart: 0,
          xStep: xFactor,
          yStart: 100,
          yStep: 1,
          zValues: firstSlice,
        });

        this.setupZoneDataSeries(
          sciChartSurface1,
          wasmContext1,
          // CONTEXT MUST MATCH THE SURFACE. These two were crossed: surface1
          // (wasmContext1) was handed the series built on wasmContext2 and vice
          // versa. SciChart attaches a foreign-context series without complaint
          // and then never draws it — which is exactly what the strip did: right
          // z-values, right colour map, series present, nothing on screen.
          this.heatmapDataSeries1
        );
        this.setupZoneDataSeries(
          sciChartSurface2,
          wasmContext2,
          this.heatmapDataSeries2
        );
        this.onZoneHistoricalSeriesReady();
      },
      (error) => (this.errorMessage = error)
    );

    // Zone RT Data Subscription
    //
    // Per poll this now does O(width) work and allocates ONE heatmap slice.
    // It previously rebuilt every retained tick's slice (O(ticks x width)) and
    // constructed two new UniformHeatmapDataSeries plus two new renderable series
    // — the series are created once in the backfill handler above and reused here.
    this.zoneRTDataSubscription = this.projectService.zoneRTData.subscribe(
      (data) => {
        if (data == null) {
          console.log('Error in Temp Comp: Zone data is null.');
          return;
        } else if (data.ZRC == null) {
          console.log('Error in Temp Comp: Zone data > ZRC is null.');
          return;
        }

        if (data.ZRC.length === 0) {
          return;
        }

        const startDataPoint = this.tempData.LSS;
        const endDataPoint = this.tempData.LES;

        // Ingest every row the server returned, not just [0] — a poll interval
        // that spans more than one tick would otherwise drop the remainder.
        // The store appended these rows; heatMaps follows in onZoneRowsChanged
        // so the projection can never run against an unwritten buffer.

        // DRAW IT. The zone surface only ever rendered from a tickPosition
        // emission, and nothing emits one on load — main-layout's
        // updateChartDataForTick('0') is commented out and setTickToEnd is never
        // called. So the slices were built, retained, and never handed to
        // setZValues: the strip stayed dark, and an overheat sitting in the data
        // as ZR code 4 was invisible until the operator happened to scrub.
        //
        // Rendering the newest retained slice directly, rather than emitting a
        // global tick, keeps this to the zone surface and cannot disturb the
        // temperature trace or the slider position.
        this.renderNewestZoneSlice();

        console.log(
          'temp comp > pushed ' +
            data.ZRC.length +
            ' Zone row(s). Ticks: ' +
            this.zoneDataRows.totalCount +
            ' retained: ' +
            this.zoneDataRows.retainedCount
        );
      },
      (error) => (this.errorMessage = error)
    );

    // Tick now arrives through ChartTickService — ONE subscription for every
    // chart surface, so a screen cannot be added without following the slider.
    this.onRangeSliderChange = (left, right) => {
      //console.log(left + '-' + right);
      const linerLength = this.tempData.CRE - this.tempData.CRB;

      const visibleLeft = Math.round((left / 100) * linerLength);
      const visibleRight = Math.round((right / 100) * linerLength);

      tempXAsix.visibleRange = new NumberRange(visibleLeft, visibleRight);
      xAxis.visibleRange = new NumberRange(visibleLeft, visibleRight);
      xAxis2.visibleRange = new NumberRange(visibleLeft, visibleRight);
    };
  }

  /** True once the real zone series exist; the placeholders have no methods. */
  private zoneSeriesReady(): boolean {
    return (
      typeof this.heatmapDataSeries1?.setZValues === 'function' &&
      typeof this.heatmapDataSeries2?.setZValues === 'function'
    );
  }

  /**
   * Draw the newest retained zone slice.
   *
   * Called both when zone data lands AND when the surfaces finish building,
   * because either can happen second. Without the second call, data that arrived
   * during the async surface creation was never drawn at all.
   */
  /**
   * The historical zone series are built and attached — THE THIRD CALL SITE.
   *
   * renderNewestZoneSlice() had exactly two callers and neither covers a freshly
   * loaded static project:
   *
   *   onZoneRowsChanged (changed$('zone'))  runs BEFORE these series exist, so
   *                                         zoneSeriesReady() is false — the
   *                                         placeholders are `{}` until this
   *                                         block replaces them — and it returns
   *                                         without drawing
   *   the zoneRTData handler                covers LIVE cures, on every realtime
   *                                         push, and never fires for a project
   *                                         that has finished
   *
   * So the series were constructed with `zValues: firstSlice` — tick 0 — and
   * nothing ever replaced that. MEASURED on two projects by comparing the bound
   * array against both candidates: E2E-20260918-103636 bound === first (tick 0)
   * with newest at tick 17; S3640 REV_1 bound === first and ALL-ZERO, while its
   * newest carries four codes across 514 of 514 cells. On S3640 that means the
   * strip is blank while an overheat sits one index away.
   *
   * This is NOT a regression of 218673c. That commit fixed the same symptom on
   * the same surface for a different cause — nothing emitted a tick on load — and
   * put its call in the payload handler of its time. The refactor that introduced
   * renderNewestZoneSlice moved construction here and left the call on two paths
   * that no longer carry a fresh static load. Neither guard is wrong; the call
   * site was missing.
   *
   * 218673c's comment is still literally true — "invisible until the operator
   * happened to scrub" describes today exactly, because updateZoneChartForTick is
   * reachable from the slider and self-heals on the first scrub. It simply stopped
   * governing the path that matters.
   *
   * disposeZoneDataSeries() above touches neither heatMaps nor the retained rows
   * nor lastHeatMapIndex, so `newest` is current at this point rather than stale.
   */
  private onZoneHistoricalSeriesReady(): void {
    this.zoneDataLoaded = true;
    this.renderNewestZoneSlice();
  }

  private renderNewestZoneSlice(): void {
    const newest = this.heatMaps.nextIndex - 1;
    if (newest >= 0 && this.zoneSeriesReady()) {
      this.updateZoneChartForTick(String(newest));
    }
  }

  setupZoneDataSeries(
    sciChartSurface: SciChartSurface,
    wasmContext: TSciChart,
    heatMapDataSeries: UniformHeatmapDataSeries
  ) {
    const heatmapSeries = new UniformHeatmapRenderableSeries(wasmContext, {
      dataSeries: heatMapDataSeries,
      colorMap: new HeatmapColorMap({
        minimum: 0, // min value in the zValues (data) to map to offset 0 in the colormap. see GraphViewModel.cs ZoneGradient is define there and is hard coded.
        maximum: 4, // max value in the zValues (data) to map to offset 1 in the colormap
        // Already matched the manifest exactly; shared now so it cannot drift
        // from the other surfaces drawing the same zone states.
        gradientStops: ZONE_GRADIENT_STOPS,
      }),
    });

    sciChartSurface.renderableSeries.add(heatmapSeries);
  }

  updateZoneChartForTick(val: string) {
    const sliderVal: number = +val;
    this.tick = sliderVal;

    // The zone series are `{} as UniformHeatmapDataSeries` until the ASYNC
    // createZoneGraphs replaces them. Zone data can land first, and calling
    // clear()/setZValues() on the placeholder throws — which in an RxJS
    // next-handler tears the subscription down, so the strip never paints again.
    // Exactly the trap the curing chart hit.
    if (!this.zoneSeriesReady()) {
      return;
    }

    // Clear Graph
    if (this.zoneDataLoaded) {
      this.heatmapDataSeries1.clear();
      this.heatmapDataSeries2.clear();
    }

    if (this.heatMaps.totalCount == 0) {
      this.zoneDataLoaded = false;
      return;
    }

    // THE SLIDER RANGE COMES FROM TEMPERATURE, NOT FROM THIS FAMILY.
    //
    // tickMax is data.TRC.length - 1, and the zone payload routinely holds FEWER
    // rows than temperature. So the last ticks of the slider have no zone row —
    // not because anything was evicted, but because they never existed here. The
    // old code read that as missing history and asked for a full reload, which
    // could never satisfy it: the reload returns the same shorter zone payload,
    // the next render asks again, and the 15 s throttle turns it into a steady
    // ~4 reloads per load. That is the cascade.
    //
    // Clamp into what this family actually has and draw the nearest row.
    let zoneTick = sliderVal;
    if (zoneTick >= this.heatMaps.nextIndex) {
      zoneTick = this.heatMaps.nextIndex - 1;
    }
    if (zoneTick < 0) {
      zoneTick = 0;
    }
    let slice = this.heatMaps.get(zoneTick);
    if (slice == null) {
      const fallback = this.heatMaps.nearest(zoneTick);
      slice = fallback?.row;
    }
    if (slice == null) {
      return;
    }
    this.tempDataLoaded = true;

    // Update Heatmap Graph — mutates the existing series in place; no new
    // UniformHeatmapDataSeries is constructed here.
    this.heatmapDataSeries1.setZValues(slice);
    this.heatmapDataSeries2.setZValues(slice);
  }

  setRollOver(val: boolean) {
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = false;
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = false;
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = val;
  }
  setPan(val: boolean) {
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = false;
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = false;
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = val;
  }
  setZoom(val: boolean) {
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = false;
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = false;
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = val;
  }

  private dblClickBound = false;

  /**
   * Put the slider thumbs back to full span.
   *
   * They are not driven by the axis, so when the axis was fitted back to full the
   * thumbs stayed where the user left them — the "stuck" slider. Resetting them
   * with the axis keeps the two telling the same story.
   */
  private resetRangeSlider(): void {
    this.rangeSliderLeftVal = 0;
    this.rangeSliderRightVal = 100;
  }

  // ── returning to live ──────────────────────────────────────────────────────
  //
  // Joel's ruling (option 1): hold position while scrubbed, and give an explicit way
  // back. Web already held position — that IS the zoom latch, and it is also Rob's
  // original complaint, "the slider will not return you to the latest tick". The
  // holding was never the missing half; the way forward again was.
  //
  // Deliberately SEPARATE from zoomOut() even though it currently delegates to it.
  // zoomOut is a zoom control that happens to reset the latch; "return to live" is a
  // different user intent, and conflating the two is exactly why there appeared to
  // be no way back. Keeping them distinct also means the affordance can be labelled
  // and placed for what it does.

  /** True while the view is following the newest data. Drives the affordance. */
  get followingLive(): boolean {
    return this.zoomLatch.isFollowing;
  }

  /** True when the user is behind AND data has arrived since. */
  get newDataBehind(): boolean {
    return this.zoomLatch.hasNewDataBehind;
  }

  /** Re-enable follow and re-frame onto the newest data. */
  returnToLive(): void {
    this.zoomOut();
  }

  zoomOut() {
    if (!this.onZoomOut) return;
    this.onZoomOut();
  }

  addMarker() {
    if (!this.onAddMarker) return;
    this.onAddMarker();
  }

  /** Delegates to the ONE marker colour resolver; see services/marker-color.ts. */
  convertRgbStringToColor(rgbString: string): string {
    return markerColor(rgbString);
  }

  updateVerticalAnnotations(sciChart: SciChartSurface, markers: MarkerInfo[]) {
    console.log('=== updateVerticalAnnotations START ===');
    console.log('updateVerticalAnnotations called with markers:', markers);
    console.log('Number of markers:', markers?.length);
    console.log('SciChart surface:', sciChart);
    console.log('Current annotations count before clear:', sciChart.annotations.size());
    
    // Clear all annotations except the boxAnnotation and the fault severity bands
    const annotationsToRemove: any[] = [];
    sciChart.annotations.asArray().forEach(ann => {
      if (
        ann !== this.boxAnnotation &&
        ann !== this.overheatAnnotation &&
        !this.faultBandAnnotations.includes(ann as BoxAnnotation)
      ) {
        annotationsToRemove.push(ann);
      }
    });
    annotationsToRemove.forEach(ann => sciChart.annotations.remove(ann));
    console.log('Marker annotations cleared, keeping box annotation');

    if (!markers || markers.length === 0) {
      console.log('No markers to add - exiting');
      console.log('=== updateVerticalAnnotations END ===');
      return;
    }

    markers.forEach((mar, index) => {
      console.log(`Processing marker ${index}:`, mar);
      
      // Convert RGB string "r, g, b" to format SciChart understands
      const colorString = this.convertRgbStringToColor(mar.C);
      console.log(`  Position: ${mar.P}`);
      console.log(`  Color: "${mar.C}" -> converted to "${colorString}"`);
      
      const annt = new VerticalLineAnnotation({
        labelPlacement: ELabelPlacement.Axis,
        showLabel: true,
        stroke: colorString,
        strokeThickness: 3,
        x1: metresToDisplay(mar.P, this.projectService.isMetricUnit),
        axisLabelFill: colorString,
        axisFontSize: 14,
        isEditable: true,
        isSelected: false,
        onDragStarted: () => {
          // Increase thickness during drag for better visibility
          annt.strokeThickness = 6;
        },
        onDragEnded: () => {
          // annt.x1 is in AXIS units; P is persisted in metres.
          const metres = +displayToMetres(
            annt.x1,
            this.projectService.isMetricUnit
          ).toFixed(2);
          // Update the position in the internal array
          mar.P = metres;
          // Persist to API
          this.projectService.updateVerticalAnnotationPosition(index, metres);
          // Reset to normal thickness after drag
          annt.strokeThickness = 3;
        },
      });

      console.log(`  Adding annotation to chart for marker ${index}`);
      sciChart.annotations.add(annt);
      console.log(`  Annotation added successfully. Total annotations: ${sciChart.annotations.size()}`);
    });
    
    console.log('Final annotations count:', sciChart.annotations.size());
    console.log('=== updateVerticalAnnotations END ===');
  }

  /**
   * Shade everything above the overheat limit.
   *
   * Hidden when OT is absent or zero: a band anchored at 0 would paint the whole
   * plot and read as a permanent alarm. The top is generous rather than exact —
   * it only has to cover whatever the trace reaches, and y auto-ranges to data.
   */
  private updateOverheatArea(): void {
    const annot = this.overheatAnnotation;
    if (!annot) {
      return;
    }
    const ot = this.tempData?.OT ?? 0;
    if (!(ot > 0)) {
      annot.isHidden = true;
      return;
    }
    annot.isHidden = false;
    annot.x1 = 0;
    annot.x2 = this.tempData.CRE;
    annot.y1 = ot;
    annot.y2 = ot + 1000;
  }

  /**
   * The curing range: CT at the bottom, OT at the top.
   *
   * THE TOP WAS A HARDCODED `y1Val + 150`. That is not a range anybody set — on
   * the project this was measured against it drew 60 -> 210 C for CT=60/OT=140,
   * so the "curing range" swamped the plot, ran 70 degrees past the overheat
   * limit, and overlapped the red overheat wash for its entire upper half. With a
   * trace at ~42 C the whole visible area was band, which is why Rob reported the
   * green bar as MISSING: it was there and it was everything.
   *
   * Desktop parity (viscom, HorizontalTempAnnotation + Curing.xaml): the green
   * wash spans CT to OT and the red wash starts exactly where it ends. There is
   * no fixed height — the proportion is data-driven, (OT - CT) over the axis
   * span — which is why a constant could never have been right for two projects
   * with different limits.
   *
   * HIDDEN WHEN THE RANGE IS NOT A RANGE. If OT is absent, zero, or not above
   * CT, there is no band to draw and an inverted or zero-height box is worse than
   * nothing: it would either vanish confusingly or paint from the top down.
   * Declining is the honest answer, and it matches how updateOverheatArea already
   * treats a missing OT.
   */
  updateBoxArea(annot: BoxAnnotation, y1Val: number) {
    const ot = this.tempData?.OT ?? 0;
    if (!(ot > y1Val)) {
      annot.isHidden = true;
      return;
    }
    annot.isHidden = false;
    annot.x1 = 0;
    annot.x2 = this.tempData.CRE;
    annot.y1 = y1Val;
    annot.y2 = ot;
  }

  /**
   * Desktop parity (GraphViewModel.UpdateHoizontalLossAnnotations / the
   * HorizontalFiberFaultAnnotation on Overview.xaml): one full-height,
   * severity-coloured band per active severity at the displayed tick, spanning
   * from the severity's first fault position to the liner end. Solid low-opacity
   * fill stands in for the Desktop's 45° hatch brush (no hatch in SciChart JS).
   */
  updateFaultSeverityBands(sciChart: SciChartSurface) {
    this.faultBandAnnotations.forEach((ann) => {
      if (sciChart.annotations.contains(ann)) {
        sciChart.annotations.remove(ann);
      }
    });
    this.faultBandAnnotations = [];

    if (this.lossDataRows.totalCount === 0 || this.lossData.LES == null) {
      return;
    }

    // Clamp the scrubbed tick into the loss rows (slider range comes from TRC,
    // which can be longer than LRC).
    let tick = this.tick;
    if (tick >= this.lossDataRows.nextIndex) {
      tick = this.lossDataRows.nextIndex - 1;
    }
    if (tick < 0) {
      tick = 0;
    }
    // NOT-YET-ARRIVED IS NOT EVICTED.
    //
    // Loss is fetched LAST in the cascade — temperature publishes, which triggers
    // zone, which triggers loss — so every render between the temperature
    // backfill and the loss one finds this buffer empty. Treating that as missing
    // history asked for the whole cascade again, immediately, on the very first
    // frame of a load. Waiting costs nothing: the data is already on its way.
    if (this.lossDataRows.totalCount === 0) {
      return;
    }

    let row = this.lossDataRows.get(tick);
    if (!row) {
      row = this.lossDataRows.nearest(tick)?.row;
    }
    if (!row) {
      return;
    }

    const lss = this.lossData.LSS;
    const les = this.lossData.LES;
    const linerLen = this.lossData.CRE - this.lossData.CRB;
    if (!(les > lss) || !(linerLen > 0)) {
      return;
    }
    const xFactor = linerLen / (les - lss);

    // First (nearest) fault position per active severity at this tick.
    const minStartBySeverity = new Map<string, number>();
    dedupeRealFaults(row.FD, row.FP, row.FM).forEach((f) => {
      const existing = minStartBySeverity.get(f.FT);
      if (existing === undefined || f.PS < existing) {
        minStartBySeverity.set(f.FT, f.PS);
      }
    });

    minStartBySeverity.forEach((ps, ft) => {
      const color = faultSeverityColor(ft);
      const xStart = Math.max(0, Math.min((ps - lss) * xFactor, linerLen));
      const band = new BoxAnnotation({
        fill: color,
        stroke: color,
        strokeThickness: 1,
        opacity: 0.3,
        x1: xStart,
        x2: linerLen,
        // Full chart height, independent of the y data range.
        yCoordinateMode: ECoordinateMode.Relative,
        y1: 0,
        y2: 1,
      });
      sciChart.annotations.add(band);
      this.faultBandAnnotations.push(band);
    });
  }

  toggleRangeSlider() {
    this.showRangeSlider = !this.showRangeSlider;

    if (!this.onRangeSliderChange) return;
    this.onRangeSliderChange(0, 100);
  }

  updateChartVisibleRange(leftVal: string, rightVal: string) {
    if (!this.onRangeSliderChange) return;

    if (+leftVal <= +rightVal) {
      this.rangeSliderLeftVal = +leftVal;
      this.rangeSliderRightVal = +rightVal;
    } else {
      this.rangeSliderLeftVal = +rightVal;
      this.rangeSliderRightVal = +leftVal;
    }

    // SLIDER ALL THE WAY RIGHT IS ALSO "RETURN TO LIVE", per the ruling — the user
    // should not have to find a button to undo a gesture they can simply reverse.
    // Checked on the FULL span, not just the right handle: a window that ends at the
    // newest tick but starts halfway is still a window, not live.
    //
    // The decision is made in runRangeSliderFrame, AFTER the coalesced axis write.
    // Resetting here instead is the bug the bench found: the write re-arms the latch.
    this.rangeFrames.submit([this.rangeSliderLeftVal, this.rangeSliderRightVal]);
  }

  toggleHelpOverlay() {
    this.projectService.isHelpOverlayVisible =
      !this.projectService.isHelpOverlayVisible;
  }

  adjustYAxisVisibleRange(yAxis_Temp: NumericAxis) {
    if (this.zoomLatch.isZoomed) {
      return;
    }
    var visibleMin = 0;
    if (yAxis_Temp.visibleRange.min < -10) {
      visibleMin = -10;
    }
    if (yAxis_Temp.visibleRange.max < this.tempAboveXPoint + 10) {
      yAxis_Temp.visibleRange = new NumberRange(
        visibleMin,
        this.tempAboveXPoint + 10
      );
    } else if (yAxis_Temp.visibleRange.min < -10) {
      yAxis_Temp.visibleRange = new NumberRange(
        visibleMin,
        yAxis_Temp.visibleRange.max
      );
    }
  }
}
